import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserTier } from '../../hooks/useUserTier';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import ComingSoonBanner from '../ui/coming-soon-banner';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  LayoutDashboard,
  FileText,
  Image,
  Users,
  Sparkles,
  Calendar,
  ListTodo,
  DollarSign, // Added DollarSign
  Crown, // Added Crown for tier management
  UserCog, // Added for User Management
  UserCheck, // Added for Team Management
  Zap, // Added for One-Click Creator
  TrendingUp, // Added for Launch Success
  Share, // For social publishing
  BarChart3, // For analytics
  Clock, // For scheduling
  Settings, // For feature flags
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';


const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTier, isFreeTier } = useUserTier();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { flags } = useFeatureFlags();

  const navigation = [
    { name: 'My Brand', href: '/app/clients', icon: Users },
    { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
    { name: 'Team', href: '/app/team', icon: UserCheck },
    {
      name: 'Content Hub',
      href: '/app/content-hub',
      icon: Sparkles,
      children: [
        { name: 'Image Studio', href: '/app/content-hub/image-studio' },
        { name: '3D Studio', href: '/app/content-hub/3d-studio' },
        { name: 'AR Studio', href: '/app/content-hub/ar-studio' },
        { name: 'Video Studio', href: '/app/content-hub/video-studio' },
        { name: 'Tasks', href: '/app/content-creation/tasks' },
      ],
    },
    { name: 'Media Library', href: '/app/media', icon: Image },
    { name: 'Posts', href: '/app/posts', icon: FileText },
    { name: 'One-Click Creator', href: '/app/one-click-creator', icon: Zap },
    {
      name: 'Content Calendar',
      href: '/app/schedule',
      icon: Calendar
    },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/app/admin/dashboard', icon: LayoutDashboard },
    { name: 'Launch Success', href: '/app/admin/launch-success', icon: TrendingUp },
    { name: 'Feature Flags', href: '/app/admin/feature-flags', icon: Settings },
    { name: 'User Management', href: '/app/admin/users', icon: UserCog },
    { name: 'Tier Management', href: '/app/admin/tiers', icon: Crown },
    { name: 'AI Models', href: '/app/admin/ai-models', icon: Zap },
    { name: 'AI Task Costs', href: '/app/admin/ai-task-costs', icon: DollarSign },
  ];

  // Track click timing for double-click detection
  const [lastClickTime, setLastClickTime] = useState<{ [key: string]: number }>({});
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});

  // Collapse items when navigating away from their sections
  useEffect(() => {
    setExpandedItems(prev => {
      const newExpanded = { ...prev };

      // Check each navigation item with children
      navigation.forEach(item => {
        if (item.children) {
          // If current path doesn't start with this item's href, collapse it
          if (!location.pathname.startsWith(item.href)) {
            newExpanded[item.name] = false;
          }
        }
      });

      return newExpanded;
    });
  }, [location.pathname]);

  const handleNavClick = (item: any, event: React.MouseEvent) => {
    // Close mobile sidebar when navigating
    closeMobileSidebar();

    const now = Date.now();
    const lastClick = lastClickTime[item.name] || 0;
    const timeDiff = now - lastClick;

    // If it's a double-click (within 300ms) and the item has children
    if (timeDiff < 300 && item.children) {
      event.preventDefault();

      // If currently on a child page, navigate back to dashboard
      if (location.pathname.startsWith(item.href) && location.pathname !== item.href) {
        navigate('/app/dashboard');
        setExpandedItems(prev => ({ ...prev, [item.name]: false }));
      }
    } else {
      // Single click - toggle expansion for items with children
      if (item.children) {
        setExpandedItems(prev => ({
          ...prev,
          [item.name]: location.pathname.startsWith(item.href) ? !prev[item.name] : true
        }));
      }
    }

    setLastClickTime(prev => ({ ...prev, [item.name]: now }));
  };

  const { isMobileOpen, closeMobileSidebar } = useSidebar();

  return (
    <aside className={`
      ${isCollapsed ? 'w-16' : 'w-64'} 
      bg-white/50 dark:bg-slate-900/50 border-r border-gray-100 dark:border-slate-800 h-screen backdrop-blur-xl fixed z-[90] transition-all duration-500 ease-in-out
      ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      {/* Toggle Button - Hidden on Desktop */}
      <div className="absolute -right-3 top-6 z-[95] hidden">
        <button
          onClick={toggleSidebar}
          className="w-6 h-6 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 hover:bg-gray-50 dark:hover:bg-slate-700"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-3 h-3 text-gray-600 dark:text-slate-300" />
          ) : (
            <ChevronLeft className="w-3 h-3 text-gray-600 dark:text-slate-300" />
          )}
        </button>
      </div>
      <nav className="mt-8 px-4">
        <ul className={`transition-all duration-500 ease-in-out ${isCollapsed ? 'space-y-0.5' : 'space-y-1'
          }`}>
          {navigation.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.href}
                onClick={(event) => handleNavClick(item, event)}
                className={({ isActive }) =>
                  `group flex items-center ${isCollapsed ? 'px-2 py-1.5 justify-center' : 'px-3 py-2.5'
                  } text-base font-medium rounded-lg transition-all duration-500 relative ease-in-out ${isActive || (item.children && location.pathname.startsWith(item.href))
                    ? 'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/10 dark:text-blue-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-50'
                  }`
                }
                title={isCollapsed ? item.name : undefined}
              >
                <item.icon
                  className={`h-5 w-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'
                    }`}
                  aria-hidden="true"
                />
                <span className={`transition-all ease-in-out overflow-hidden whitespace-nowrap ${isCollapsed
                  ? 'opacity-0 w-0 duration-300'
                  : 'opacity-100 w-auto duration-500 delay-200'
                  }`}>
                  {item.name}
                  {item.comingSoon && flags.SHOW_COMING_SOON_FEATURES && (
                    <span className="ml-auto text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full dark:bg-blue-900/30 dark:text-blue-400">
                      Soon
                    </span>
                  )}
                </span>
              </NavLink>
              {!isCollapsed && item.children && (location.pathname.startsWith(item.href) || expandedItems[item.name]) && (
                <ul className="pl-8 space-y-1 mt-1">
                  {item.children.map((child) => (
                    <li key={child.name}>
                      <NavLink
                        to={child.href}
                        onClick={closeMobileSidebar}
                        className={({ isActive }) =>
                          `group flex items-center px-3 py-2.5 text-base font-medium rounded-lg transition-all duration-200 ${isActive
                            ? 'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/10 dark:text-blue-300'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-50'
                          }`
                        }
                      >
                        {child.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}

          {user?.is_admin && ( // Admin section
            <>
              {!isCollapsed && (
                <li className="mt-6 mb-2 text-xs font-semibold leading-4 text-gray-400 uppercase tracking-wider">
                  Admin
                </li>
              )}
              {adminNavigation.map((item) => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    onClick={closeMobileSidebar}
                    className={({ isActive }) =>
                      `group flex items-center ${isCollapsed ? 'px-2 py-1.5 justify-center' : 'px-3 py-2.5'
                      } text-base font-medium rounded-lg transition-all duration-500 ease-in-out ${isActive
                        ? 'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/10 dark:text-blue-300'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-50'
                      }`
                    }
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon
                      className={`h-5 w-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'
                        }`}
                      aria-hidden="true"
                    />
                    <span className={`transition-all ease-in-out overflow-hidden whitespace-nowrap ${isCollapsed
                      ? 'opacity-0 w-0 duration-300'
                      : 'opacity-100 w-auto duration-500 delay-200'
                      }`}>
                      {item.name}
                    </span>
                  </NavLink>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>

      {/* Tier Information */}
      {currentTier && !isCollapsed && (
        <div className="absolute bottom-4 left-4 right-4">
          <div className={`p-3 rounded-lg border ${isFreeTier
            ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            : 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">

                <span className={`text-sm font-medium ${isFreeTier ? 'text-gray-600 dark:text-gray-400' : 'text-blue-700 dark:text-blue-300'
                  }`}>
                  {currentTier.display_name}
                </span>
              </div>
              {/* Credits display */}
              {user?.credits !== undefined && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {user.credits} credits
                </span>
              )}
            </div>
            {isFreeTier && (
              <NavLink
                to="/pricing"
                className="mt-2 block text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                Upgrade to Pro →
              </NavLink>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
